/**
 * Workspace 向根层叠 UI（底栏、缩放菜单等）暴露视口尺寸与 fit/全屏回调。
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface WorkspaceChromeSnapshot {
  contentSize: { width: number; height: number };
  onFitToScreenRequest: () => void;
  isFullscreen: boolean;
  isReadonly: boolean;
  onToggleFullscreen: () => void;
  canEnterFullscreen: boolean;
}

type ChromeContextValue = {
  snapshot: WorkspaceChromeSnapshot | null;
  setSnapshot: (snapshot: WorkspaceChromeSnapshot | null) => void;
};

const CanvasWorkspaceChromeContext = createContext<ChromeContextValue | null>(null);

export function CanvasWorkspaceChromeProvider({ children }: { children: ReactNode }) {
  const [snapshot, setSnapshot] = useState<WorkspaceChromeSnapshot | null>(null);
  const value = useMemo(() => ({ snapshot, setSnapshot }), [snapshot]);
  return (
    <CanvasWorkspaceChromeContext.Provider value={value}>
      {children}
    </CanvasWorkspaceChromeContext.Provider>
  );
}

export function useWorkspaceChrome(): WorkspaceChromeSnapshot | null {
  return useContext(CanvasWorkspaceChromeContext)?.snapshot ?? null;
}

/** Workspace 挂载时注册/卸载 chrome 快照，供 CanvasDesignerOverlays 消费 */
export function useWorkspaceChromeRegistration(snapshot: WorkspaceChromeSnapshot | null) {
  const ctx = useContext(CanvasWorkspaceChromeContext);
  const setSnapshot = ctx?.setSnapshot;

  useEffect(() => {
    if (!setSnapshot) return;
    setSnapshot(snapshot);
    return () => setSnapshot(null);
  }, [
    setSnapshot,
    snapshot,
    snapshot?.contentSize.width,
    snapshot?.contentSize.height,
    snapshot?.isFullscreen,
    snapshot?.isReadonly,
    snapshot?.canEnterFullscreen,
    snapshot?.onFitToScreenRequest,
    snapshot?.onToggleFullscreen,
  ]);
}
